import React, { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DocumentVente from './DocumentVente';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { setSelectedDocument } from '../reduxStore/documentVenteSlice';
import { fetchSysTypeDocuments, setSelectedTypeDoc } from '../reduxStore/SysTypeDocumentSlice';
import { labels } from '../constants/labels';

/**
 * Création d'un document de vente — route dédiée (/facturation/nouveau?type=CODE).
 *
 * Résout le type de document depuis le `code` passé en query param et le
 * positionne dans `SysTypeDocumentSlice.selectedTypeDoc` — exactement la même
 * source que lit `DocumentVenteForm.tsx` (inchangé) aujourd'hui, qu'elle vienne
 * du filtre de liste (`SelectSysTypeDocument` dans `DocumentVenteFilter`) ou
 * d'ici. Ceci évite de dupliquer/contourner cette logique existante.
 */
const DocumentVenteCreatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('type');
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTypeDoc = useSelector((state: RootState) => state.SysTypeDocumentSlice.selectedTypeDoc);
  const sysTypeData     = useSelector((state: RootState) => state.SysTypeDocumentSlice.sysTypeData);

  // Repart toujours d'une création vierge (pas de document résiduel sélectionné).
  useEffect(() => {
    dispatch(setSelectedDocument(null));
  }, [dispatch]);

  // Charge les types si nécessaire — cas d'une arrivée directe par URL, sans être
  // passé par le filtre de la liste (qui les charge déjà via SelectSysTypeDocument).
  useEffect(() => {
    if (sysTypeData.length === 0) dispatch(fetchSysTypeDocuments());
  }, [sysTypeData.length, dispatch]);

  // Résout puis positionne le type demandé par l'URL dès qu'il est disponible.
  useEffect(() => {
    if (!code || selectedTypeDoc?.code === code) return;
    const found = sysTypeData.find(t => t.code === code);
    if (found) dispatch(setSelectedTypeDoc(found));
  }, [code, sysTypeData, selectedTypeDoc, dispatch]);

  const handleRetour = () => {
    // Même logique que DocumentVenteDetailPage : retour à l'origine réelle
    // (liste, mouvements de stock, ou autre écran futur), repli sur la liste
    // seulement si aucun historique (deep-link direct).
    if (location.key === 'default') navigate('/facturation');
    else navigate(-1);
  };

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title={`Créer ${selectedTypeDoc?.libelle ?? labels.documentVente}`}
        icon={<ReceiptLongIcon />}
        onBack={handleRetour}
        backLabel="Retour"
      />
      <AppSurface>
        <DocumentVente />
      </AppSurface>
    </AppPage>
  );
};

export default DocumentVenteCreatePage;
